import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { isEqual } from 'lodash';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { SourceId } from 'admin/sources/Sources';
import { isAuthorized } from 'auth/Auth';
import { SystemLabel } from 'common/comments2/Comments2.types';
import { useMessage, MessageActionType } from 'common/message-bar/MessageBar';
import { Overlay } from 'core/overlay/Overlay';
import { usePrevious } from 'core/util/Hook';
import { RepliesActionType, useReplies } from 'reviews2/feed/review-yeti/Replies.store';
import { ReplyEditor } from 'reviews2/feed/review-yeti/reply/editor/ReplyEditor';
import { ReviewReply } from 'reviews2/feed/review-yeti/reply/ReviewReply';
import { ResponseState } from 'reviews2/feed/review-yeti/reply/ReviewReply.types';
import { isPending, shouldManualPublish } from 'reviews2/feed/review-yeti/reply/ReviewReply.util';
import { useReview } from 'reviews2/feed/review-yeti/Review.store';
import { ReplyMode, ReviewMode, ContextProps } from 'reviews2/feed/review-yeti/Review.types';
import { publish } from 'reviews2/Reviews.api';

import 'reviews2/feed/review-yeti/replies/ReviewReplies.scss';

const DEFAULT_DISPLAY_REPLIES = 5;

export const ReviewReplies: FunctionComponent<ContextProps> = ({ context }) => {
    const { t } = useTranslation('reviews2');
    const { rid, rrid, mode } = context;
    const [{ review }] = useReview();
    const { showEditor, showReplies, repliesOptions, readOnly } = context.options!;
    const [{ replies }, dispatch] = useReplies();
    const [showReviewReplies, setShowReviewReplies] = useState(true);
    const [truncated, setTruncated] = useState<boolean>(true);
    const previousRid = usePrevious(rid);
    const previousRrid = usePrevious(rrid);
    const [, messageDispatch] = useMessage();
    const [initialized, setInitialized] = useState(false);

    const showMoreReplies = () => {
        setTruncated(false);
    };

    const showFewerReplies = () => {
        setTruncated(true);
    };

    const setReply = useCallback(() => {
        dispatch({
            type: RepliesActionType.SetReply,
            reply: review,
        });
    }, [dispatch, review]);

    const publishReply = useCallback(
        async (reply: any) => {
            const publishReq = {
                id: reply.document_id,
                parentID: reply.parent_id,
                text: reply.raw_response_text,
                media: reply.expanded_post_media,
                manualPublish: shouldManualPublish(review),
                reviewDate: review.post_created_at,
            };
            const result = await trackPromise(
                publish(reply.r4e_tenant_id, publishReq),
                `review-replies-${review?.document_id}`,
            );

            dispatch({
                type: RepliesActionType.UpdateReply,
                reply: result,
            });
            dispatch({
                type: RepliesActionType.SetEditReply,
            });
            messageDispatch({
                type: MessageActionType.ShowSuccess,
                text: t('REPLY_APPROVED'),
            });
        },
        [review, dispatch, messageDispatch, t],
    );

    useEffect(() => {
        if (replies?.length && !initialized) {
            setTruncated(replies?.length > DEFAULT_DISPLAY_REPLIES);
            setShowReviewReplies(true);
            setInitialized(true);
        }
    }, [review, replies, initialized, dispatch]);

    /*
     * edit/approve response from alert
     */
    useEffect(() => {
        if (rid && !isEqual(rid, previousRid)) {
            const reply = review.replies?.find((reply: any) => reply.document_id === rid);

            if (!reply) {
                messageDispatch({
                    type: MessageActionType.ShowWarning,
                    text: t('REPLY_NOT_FOUND'),
                });
            } else if (isPending(reply)) {
                if (mode === ReviewMode.Edit) {
                    if (reply) {
                        dispatch({
                            type: RepliesActionType.SetReply,
                            reply,
                        });
                        dispatch({
                            type: RepliesActionType.SetEditReply,
                            reply,
                        });
                        dispatch({
                            type: RepliesActionType.SetReplyMode,
                            replyMode: ReplyMode.Edit,
                        });
                    }
                } else if (mode === ReviewMode.Approve) {
                    if (isAuthorized({ actions: [ActionId.ReviewResponses.Approve] })) {
                        publishReply(reply);
                    } else {
                        messageDispatch({
                            type: MessageActionType.ShowWarning,
                            text: t('REPLY_NOT_AUTHORIZED'),
                        });
                    }
                }
            } else {
                dispatch({
                    type: RepliesActionType.SetEditReply,
                });
                dispatch({
                    type: RepliesActionType.SetReplyMode,
                    replyMode: ReplyMode.Add,
                });
                messageDispatch({
                    type: MessageActionType.ShowWarning,
                    text:
                        reply.responseState === ResponseState.Rejected
                            ? t('REPLY_ALREADY_REJECTED')
                            : t('REPLY_ALREADY_APPROVED'),
                });
            }
        }
    }, [rid, previousRid, dispatch, review, mode, publishReply, messageDispatch, t]);

    // recommended replies
    useEffect(() => {
        if (rrid && !isEqual(rrid, previousRrid) && mode === ReviewMode.Respond) {
            const reply = review.recommendedReplies?.[0];

            if (!reply) {
                messageDispatch({
                    type: MessageActionType.ShowWarning,
                    text: t('REPLY_NOT_FOUND'),
                });
            } else if (isAuthorized({ actions: [ActionId.ReviewResponses.Add] })) {
                if (reply) {
                    dispatch({
                        type: RepliesActionType.SetReply,
                        reply: {
                            sanitized_text: reply.text,
                        },
                    });
                    dispatch({
                        type: RepliesActionType.SetEditReply,
                        reply: {
                            sanitized_text: reply.text,
                        },
                    });
                    dispatch({
                        type: RepliesActionType.SetReplyMode,
                        replyMode: ReplyMode.Recommend,
                    });
                }
            } else {
                messageDispatch({
                    type: MessageActionType.ShowWarning,
                    text: t('REPLY_NOT_AUTHORIZED'),
                });
            }
        }
    }, [rrid, previousRrid, dispatch, review, mode, messageDispatch, t]);

    if (!showReplies) {
        return null;
    }

    return (
        <div className="r4e-review-replies">
            <Overlay className="r4e-reviews-overlay" area={`review-replies-${review?.document_id}`} />
            {showReviewReplies &&
                replies
                    ?.filter((reply, index) => {
                        if (truncated && index < replies.length - DEFAULT_DISPLAY_REPLIES) {
                            return false;
                        }
                        return true;
                    })
                    .map((reply) => {
                        return <ReviewReply key={reply.document_id} context={context} reply={reply} />;
                    })}
            <ToggleReplies
                count={replies?.length}
                truncated={truncated}
                showMoreReplies={showMoreReplies}
                showFewerReplies={showFewerReplies}
                displayCount={truncated ? DEFAULT_DISPLAY_REPLIES : replies.length}
                setShowReplies={setShowReviewReplies}
                showReplies={showReviewReplies}
                showToggle={repliesOptions!.showToggle}
            />
            {showEditor && (
                <div>
                    <div className="notranslate" role="button" tabIndex={0} onClick={setReply} onKeyPress={setReply}>
                        <ReplyEditor context={context} />
                    </div>
                    {!review?.r4e_label_ids?.includes(SystemLabel.NoResponseRequired) &&
                        review?.review_source_id === SourceId.Booking &&
                        !readOnly && (
                            <div className="text-warning mt-2">
                                <i className="icon icon-exclamation-circle mr-1" />
                                <span>{t('BOOKING_VALIDATION_WARNING')}</span>
                            </div>
                        )}
                </div>
            )}
        </div>
    );
};

const ToggleReplies: FunctionComponent<{
    count: number;
    displayCount: number;
    showReplies: boolean;
    showToggle?: boolean;
    truncated: boolean;
    setShowReplies: (show: boolean) => void;
    showMoreReplies: () => void;
    showFewerReplies: () => void;
}> = ({ count, truncated, displayCount, showReplies, showMoreReplies, showFewerReplies, showToggle }) => {
    const { t } = useTranslation('reviews2');

    if (!showReplies || !showToggle || !count || count <= DEFAULT_DISPLAY_REPLIES) {
        return null;
    }

    return (
        <div className="r4e-review-replies-hide">
            <div>
                <Button variant="link" onClick={() => (truncated ? showMoreReplies() : showFewerReplies())}>
                    {truncated ? t('SHOW_MORE_REPLIES') : t('SHOW_FEWER_REPLIES')}
                </Button>
            </div>
            <div>{t('REPLY_COUNT', { current: displayCount, count })}</div>
        </div>
    );
};
